import React, {
  forwardRef,
  type ComponentProps,
  useEffect,
  useMemo,
  useState,
  useId,
  useCallback,
} from "react";

import {
  Autocomplete,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { InputMask } from "@react-input/mask";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";

import { BaseLabel, DateInput, Drawer, GeneralInput } from "components";
import { isProduct, ProductId } from "multitenancy/product";

import { useAppSelector } from "store/rootReducer";

import { StyledAutocomplete } from "components/ProcedureAttendant/style";

import { procedureCustomerAttendantService } from "services/attendant";
import { HearingRoleService } from "services/hearingRole";
import { HearingSourceService } from "services/hearingSource";

import { buildMaskConfig } from "utils/mask";

import { CreateRecordConstants } from "./contants";
import { IHearingFilter } from "./interface";
import {
  ContainerAllFilter,
  ContainerFormFilter,
  SimpleDropDownDiv,
  RadioBoxContainer,
  RadioBoxContainerBottom,
  DateInputWrapper,
  ContainerAutoComplete,
} from "./styles";

const MaskedGeneralInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof GeneralInput>
>((inputProps, ref) => <GeneralInput {...inputProps} inputRef={ref} />);

MaskedGeneralInput.displayName = "MaskedGeneralInput";

export const HearingFilter: React.FC<IHearingFilter> = ({
  title,
  hearingNumber,
  setHearingNumber,
  startDate,
  setStartDate,
  finishDate,
  setFinishDate,
  setValueOptionDropDown,
  active,
  setActive,
  showDrawer,
  setShowDrawer,
  anchor = "right",
  onClickSearch,
  onClickClear,
  options,
  clearDates,
  startDateError,
  finishDateError,
  onClose,
  createAudienceMask,
  isCounty,
  setIsCounty,
  hearingTypeId,
  setHearingTypeId,
  hearingSourceId,
  setHearingSourceId,
  status,
  setStatus,
  setAttendantFilterId,
  attendantFilterId,
}) => {
  const { t } = useTranslation();
  const MAX_CHARS = 50;
  const [inputValue, setInputValue] = useState<string>("");
  const [hearingTypes, setHearingTypes] = useState<any[]>([]);
  const [hearingSources, setHearingSources] = useState<any[]>([]);
  const isInquerito = isProduct(ProductId.Inquerito);
  const { user } = useAppSelector((state) => state.user);
  const [scopeSelection, setScopeSelection] = useState<
    "current" | "county" | "general"
  >(isCounty ? "county" : "current");

  const hasParticipant = !!attendantFilterId;
  const [loadingAttendant, setLoadingAttendant] = useState(false);
  const [attendantList, setAttendantList] = useState<any[]>([]);
  const [participant, setParticipant] = useState<string>("");

  useEffect(() => {
    if (!attendantFilterId) {
      setParticipant("");
      setAttendantList([]);
    }
  }, [attendantFilterId]);

  useEffect(() => {
    if (user?.customerId) {
      HearingRoleService.getAll(user.customerId).then((response) => {
        if (response.status && response.data?.data) {
          const formattedTypes = response.data.data.map((item: any) => ({
            label: item.name,
            value: item.id,
          }));
          setHearingTypes(formattedTypes);
        }
      });

      if (isInquerito) {
        HearingSourceService.getAll(user.customerId, 1).then((response) => {
          if (response.status && response.data?.data) {
            const formatted = response.data.data.map((item: any) => ({
              label: item.name,
              value: item.id,
            }));
            setHearingSources(formatted);
          }
        });
      }
    }
  }, [user?.customerId, isInquerito]);

  const statusOptionsWithAll = [
    { label: t("common.all"), value: null },
    ...options,
  ];
  const maskConfig = useMemo(
    () => buildMaskConfig(createAudienceMask),
    [createAudienceMask],
  );

  const debouncedFetch = useCallback(
    debounce((value: string) => {
      fetchParticipants(value);
    }, 1000),
    [],
  );

  const fetchParticipants = async (query: string) => {
    if (!query.trim()) {
      setAttendantList([]);
      return;
    }

    if (!user) return;

    try {
      const { data } =
        await procedureCustomerAttendantService.GetAllByNameOrDocument({
          customerId: user?.customerId,
          searchTerm: query,
          globalSearch: true,
        });
      const list = data?.data?.map((item: any) => {
        return {
          label: `${item.name ?? ""} - Documento: ${item.document || "N/A"}`,
          value: item.id,
        };
      });
      setAttendantList(list);
    } catch (error) {
      setAttendantList([]);
      console.error("Erro ao buscar participantes:", error);
    }
  };

  useEffect(() => {
    if (hasParticipant) {
      setScopeSelection("general");
    } else {
      setScopeSelection(isCounty ? "county" : "current");
    }
  }, [hasParticipant, isCounty]);

  const handleSearch = () => {
    const scopeToUse = hasParticipant ? "general" : scopeSelection;
    if (setIsCounty) {
      setIsCounty(scopeToUse === "county");
    }
    onClickSearch?.({
      attendantId: attendantFilterId || null,
      scope: scopeToUse,
    });
  };

  const handleClear = () => {
    setAttendantFilterId(null);
    setIsCounty?.(false);
    setScopeSelection("current");
    onClickClear?.();
  };

  useEffect(() => {
    setAttendantFilterId(null);
  }, [clearDates]);

  useEffect(() => {
    setLoadingAttendant(false);
  }, [attendantList]);

  const reactId = useId();
  const labelServentiaId = `${reactId}-label-serventia`;

  return (
    <Drawer
      onClose={() => {
        onClose?.();
        setShowDrawer(false);
      }}
      onSearch={handleSearch}
      onClear={handleClear}
      title={title}
      footer="filter"
      open={showDrawer}
      anchor={anchor}
      paddingLeft={0.1}
      paddingRight={0.1}
      paddingTop={0.1}
      paddingBottom={0.1}
    >
      <ContainerAllFilter>
        <ContainerFormFilter>
          {createAudienceMask ? (
            <InputMask
              component={MaskedGeneralInput}
              mask={maskConfig.mask}
              showMask
              replacement={maskConfig.replacement}
              label={t(CreateRecordConstants.HEARING_NUMBER)}
              name="hearingNumberFilter"
              onChange={setHearingNumber}
              value={hearingNumber}
              marginRight="0px"
              maxLength={90}
              required={false}
            />
          ) : (
            <GeneralInput
              label={t(CreateRecordConstants.HEARING_NUMBER)}
              name="hearingNumberFilter"
              onChange={setHearingNumber}
              value={hearingNumber}
              marginRight="0px"
              maxLength={90}
              required={false}
            />
          )}

          <DateInputWrapper>
            <DateInput
              label={t(CreateRecordConstants.START_DATE)}
              setValue={setStartDate}
              value={startDate}
              required={false}
              id="startDateFilterId"
              clear={clearDates}
              error={startDateError}
            />
          </DateInputWrapper>

          <DateInputWrapper>
            <DateInput
              label={t(CreateRecordConstants.FINISH_DATE)}
              setValue={setFinishDate}
              value={finishDate}
              required={false}
              id="finishDateFilterId"
              clear={clearDates}
              error={finishDateError}
            />
          </DateInputWrapper>

          <SimpleDropDownDiv>
            <BaseLabel marginLeft="0px" color="#666666">
              {t(CreateRecordConstants.STATUS)}
            </BaseLabel>
            <Autocomplete
              disableClearable
              value={
                statusOptionsWithAll.find((opt) => opt.value === status) ||
                statusOptionsWithAll[0]
              }
              onChange={(_event: any, newValue: any) => {
                if (setStatus) {
                  setStatus(newValue?.value);
                }
                setValueOptionDropDown(newValue);
              }}
              inputValue={inputValue}
              onInputChange={(_event, newInputValue) =>
                setInputValue(newInputValue)
              }
              id="status-filter"
              options={statusOptionsWithAll}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  variant="outlined"
                  placeholder={t("common.select")}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: "#D1DBE3 !important",
                  },
                  "&:hover fieldset": { borderColor: "#CBCBCB !important" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1D9BF0 !important",
                  },
                },
              }}
            />
          </SimpleDropDownDiv>

          <SimpleDropDownDiv>
            <BaseLabel marginLeft="0px" color="#666666">
              {t("common.type")}
            </BaseLabel>
            <Autocomplete
              value={
                hearingTypes.find((type) => type.value === hearingTypeId) ||
                null
              }
              onChange={(_event: any, newValue: any) => {
                if (setHearingTypeId) {
                  setHearingTypeId(newValue?.value || null);
                }
              }}
              options={hearingTypes}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  variant="outlined"
                  placeholder={t("common.select")}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: "#D1DBE3 !important",
                  },
                  "&:hover fieldset": { borderColor: "#CBCBCB !important" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1D9BF0 !important",
                  },
                },
              }}
            />
          </SimpleDropDownDiv>

          {isInquerito && (
            <SimpleDropDownDiv>
              <BaseLabel marginLeft="0px" color="#666666">
                {t("filter.procedureSource")}
              </BaseLabel>
              <Autocomplete
                value={
                  hearingSources.find((s) => s.value === hearingSourceId) ||
                  null
                }
                onChange={(_event: any, newValue: any) => {
                  if (setHearingSourceId) {
                    setHearingSourceId(newValue?.value || null);
                  }
                }}
                options={hearingSources}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    variant="outlined"
                    placeholder={t("common.select")}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      borderWidth: "2px",
                      borderColor: "#D1DBE3 !important",
                    },
                    "&:hover fieldset": { borderColor: "#CBCBCB !important" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1D9BF0 !important",
                    },
                  },
                }}
              />
            </SimpleDropDownDiv>
          )}

          <ContainerAutoComplete>
            <BaseLabel marginLeft="0px" color="#666666">
              {t("common.participant")}
            </BaseLabel>
            <StyledAutocomplete
              value={participant}
              loading={loadingAttendant}
              style={{ marginTop: 0 }}
              options={attendantList || []}
              noOptionsText={t("common.noOptionsFound")}
              onInputChange={(_e, newInputValue) => {
                const limited = newInputValue.slice(0, MAX_CHARS);
                const display =
                  newInputValue.length > MAX_CHARS ? `${limited}...` : limited;
                setParticipant(display);

                if (limited.trim().length >= 3) {
                  setLoadingAttendant(true);
                  debouncedFetch(limited);
                }
                if (limited.trim().length === 0) {
                  setAttendantList([]);
                }
              }}
              onChange={(_, option: any) => {
                if (!option) {
                  setAttendantFilterId(null);
                  return;
                }
                const label = option.label ?? String(option.value);
                const limited = label.slice(0, MAX_CHARS);
                const display =
                  label.length > MAX_CHARS ? `${limited}...` : limited;
                setParticipant(display);
                setAttendantFilterId(option.value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t("common.typeNameOrDocument")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAttendant ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </ContainerAutoComplete>

          <BaseLabel id={labelServentiaId} marginLeft="0px" color="#666666">
            {t("filter.registry")}
          </BaseLabel>

          {hasParticipant && (
            <div style={{ margin: "4px 0 8px", fontSize: 12, color: "#666" }}>
              {t("filter.participantFilterActive")}
            </div>
          )}

          <RadioBoxContainer
            style={{ marginTop: "0" }}
            aria-labelledby={labelServentiaId}
          >
            <RadioGroup
              value={hasParticipant ? "general" : scopeSelection}
              onChange={(e) => {
                if (hasParticipant) return;
                const value = e.target.value as
                  | "current"
                  | "county"
                  | "general";
                setScopeSelection(value);
                setIsCounty?.(value === "county");
              }}
              row
              sx={{
                "& .MuiFormControlLabel-root": { marginRight: "24px" },
                "& .MuiRadio-root": {
                  color: "#666666",
                  "&.Mui-checked": { color: "#1D9BF0" },
                },
              }}
            >
              <FormControlLabel
                value="current"
                control={<Radio size="small" />}
                label={t("filter.currentCourt")}
                disabled={hasParticipant}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <FormControlLabel
                value="county"
                control={<Radio size="small" />}
                label={t("filter.byCounty")}
                disabled={hasParticipant}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <FormControlLabel
                value="general"
                control={<Radio size="small" />}
                label={t("filter.general")}
                disabled
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
            </RadioGroup>
          </RadioBoxContainer>

          <BaseLabel marginLeft="0px" color="#666666">
            {t("common.status")}
          </BaseLabel>
          <RadioBoxContainerBottom style={{ marginTop: "0" }}>
            <RadioGroup
              value={
                active === true
                  ? "active"
                  : active === false
                    ? "inactive"
                    : "all"
              }
              onChange={(e) => {
                if (e.target.value === "active") setActive(true);
                else if (e.target.value === "inactive") setActive(false);
                else setActive("all");
              }}
              row
              sx={{
                "& .MuiFormControlLabel-root": { marginRight: "24px" },
                "& .MuiRadio-root": {
                  color: "#666666",
                  "&.Mui-checked": { color: "#1D9BF0" },
                },
              }}
            >
              <FormControlLabel
                value="active"
                control={<Radio size="small" />}
                label={t("filter.activeStatus")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <FormControlLabel
                value="inactive"
                control={<Radio size="small" />}
                label={t("filter.inactiveStatus")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={t("filter.allStatus")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
            </RadioGroup>
          </RadioBoxContainerBottom>
        </ContainerFormFilter>
      </ContainerAllFilter>
    </Drawer>
  );
};
